/**
 * useRecipientRowCounts
 *
 * Live pre-flight for the per-target send (SYD-2410): per selected target, how
 * many rows belong to it and survive the operator's ticks and column filters,
 * summed over every table the send's documents render. `WorkflowParameterContent`
 * is its only consumer — it turns a zero into a refusal, so a recipient never
 * receives a document with no rows of theirs in it.
 *
 * Live on every input — the rows collections, the ticked selection, the column
 * filters and the table types' columns — so the count on screen follows a tick
 * immediately and the click-time gate reads the same value.
 *
 * Fails closed on missing evidence: while a listener has not delivered, while
 * the table types are in flight, or while `parentPath` is unresolved, `loading`
 * is true and the counts are empty; a listener that errors lands its table in
 * `unreadableTableLabels` and empties the counts, because counting an unreadable
 * table as zero rows would name innocent recipients as empty.
 *
 * The rows query carries no `limit` and no `orderBy`: order is irrelevant to a
 * count, and a cap would make a target whose rows sit past it read as empty and
 * refuse a valid send. The rows buffer is keyed by `formStore.tableKey`, so a
 * `parentPath` change cannot let one form's rows be counted as another's.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { collection, onSnapshot } from 'firebase/firestore';
import type { ArRowData, ArRowId, ArTableType, TypeId } from '#types';
import { AR_TABLE_AGGREGATIONS_ROW_ID, arTableRowsPath } from '#types';
import { firestore } from '@/services/startFirestore';
import { formStore, useStoreDocsByPaths, type DocEntry } from '@/store';
import { createSplitQueryFromTargetSource } from '@/use/form/useFormSplit';
import { filterArRowIdsForRecipient } from '@/components/ArTable/split/filterArRowIdsForRecipient';

type TableRows = {
  rowIds: ArRowId[];
  rowsDocs: Map<string, DocEntry<ArRowData>>;
};

type UseRecipientRowCountsParams = {
  /** The fan-out declaration — `perTargetSplit`. `undefined` disables the hook and binds nothing. */
  split: { tableId: string; columnId: string } | undefined;
  /** The targets the send fans out over — `selectedTargetIds`. */
  targetIds: readonly string[];
  /** Tables the send renders, across every export view — `exportTableIds`. */
  tableIds: readonly string[];
  /** `forms/{formId}/formVersions/{versionId}` of the form on screen. `undefined` before it loads. */
  parentPath: string | undefined;
};

type UseRecipientRowCountsResult = {
  loading: boolean;
  /**
   * Tables whose rows listener failed, named as the operator sees them — the same
   * `description.shortLabel` `useSplitColumnWarnings` prints, so two banners in one
   * sidebar cannot name one table two ways. Non-empty means no count is trustworthy.
   */
  unreadableTableLabels: string[];
  /** Per target, the rows that belong to it and survive the ticks, summed over `tableIds`. */
  rowCountByTargetId: Record<string, number>;
};

export function useRecipientRowCounts({
  split,
  targetIds,
  tableIds,
  parentPath,
}: UseRecipientRowCountsParams): UseRecipientRowCountsResult {
  const splitTableId = split?.tableId;
  const splitColumnId = split?.columnId;
  const enabled = splitTableId !== undefined && splitColumnId !== undefined;

  /**
   * Both id lists arrive as a fresh array on every render — `selectedTargetIds` is
   * memoized on `Form.useWatch`'s per-keystroke object — so the whole walk below
   * would recompute on every keystroke in an unrelated text parameter. A joined
   * key is the identity that actually changed.
   */
  const tableIdsKey = useMemo(() => [...new Set(tableIds)].sort().join('|'), [tableIds]);
  const boundTableIds = useMemo(
    () => (tableIdsKey === '' ? [] : tableIdsKey.split('|')),
    [tableIdsKey],
  );
  const targetIdsKey = useMemo(() => targetIds.join('|'), [targetIds]);
  const boundTargetIds = useMemo(
    () => (targetIdsKey === '' ? [] : targetIdsKey.split('|')),
    [targetIdsKey],
  );

  /**
   * The rows buffer's keys, the same `formStore.tableKey` the selection slots use.
   * Keying by the table id alone would let the previous form's rows answer a
   * lookup for one render after `parentPath` moves, with `loading` already false.
   */
  const boundTableKeys = useMemo(() => {
    const path = parentPath;
    if (path === undefined) return [];
    return boundTableIds.map((tableId) => ({ tableId, tableKey: formStore.tableKey(path, tableId) }));
  }, [parentPath, boundTableIds]);

  const typePaths = useMemo(
    () => (enabled ? boundTableIds.map((tableId) => `tableTypes/${tableId}`) : []),
    [enabled, boundTableIds],
  );
  const { data: tableTypes, loading: typePathsLoading } = useStoreDocsByPaths<ArTableType>(typePaths);
  const typesLoading = typePaths.length > 0 && typePathsLoading;

  const [rowsByTableKey, setRowsByTableKey] = useState<Record<string, TableRows>>({});
  const [errorByTableKey, setErrorByTableKey] = useState<Record<string, Error>>({});

  useEffect(() => {
    const path = parentPath;
    if (!enabled || path === undefined || boundTableKeys.length === 0) return;

    const unsubscribes = boundTableKeys.map(({ tableId, tableKey }) => onSnapshot(
      collection(firestore, arTableRowsPath(path, tableId)),
      (snap) => {
        const docs = snap.docs.filter((rowDoc) => rowDoc.id !== AR_TABLE_AGGREGATIONS_ROW_ID);
        setRowsByTableKey((prev) => ({
          ...prev,
          [tableKey]: {
            rowIds: docs.map((rowDoc) => rowDoc.id),
            rowsDocs: new Map(docs.map((rowDoc) => [rowDoc.id, { data: rowDoc.data() as ArRowData }])),
          },
        }));
      },
      (error) => {
        setErrorByTableKey((prev) => ({ ...prev, [tableKey]: error }));
      },
    ));

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [enabled, parentPath, boundTableKeys]);

  const loading = enabled
    && boundTableIds.length > 0
    && (boundTableKeys.length === 0
      || typesLoading
      || boundTableKeys.some(({ tableKey }) => (
        rowsByTableKey[tableKey] === undefined && errorByTableKey[tableKey] === undefined
      )));

  const unreadableTableLabels = useMemo(
    () => boundTableKeys
      .filter(({ tableKey }) => errorByTableKey[tableKey] !== undefined)
      .map(({ tableId }) => tableTypes.get(`tableTypes/${tableId}`)?.description?.shortLabel ?? tableId),
    [boundTableKeys, errorByTableKey, tableTypes],
  );

  const tablesSnap = useSnapshot(formStore.state.tables);

  /**
   * One read of each table's slot ahead of the per-target walk: the ticks and the
   * filter entries are the same for every target, and building the entries inside
   * the walk would allocate once per target per table for nothing.
   */
  const rowSources = useMemo(() => {
    if (!enabled) return [];
    return boundTableKeys.flatMap(({ tableId, tableKey }) => {
      const rows = rowsByTableKey[tableKey];
      if (!rows) return [];
      const slot = tablesSnap.get(tableKey);
      return [{
        rowIds: rows.rowIds,
        rowsDocs: rows.rowsDocs,
        columns: tableTypes.get(`tableTypes/${tableId}`)?.columns ?? [],
        filterEntries: Object.entries(slot ? slot.ui.filtering.byColumnId : {}).filter(
          (entry): entry is [TypeId, string] => typeof entry[1] === 'string' && entry[1].length > 0,
        ),
        selectedRowIds: slot ? slot.ui.selection.selectedRowIds : null,
      }];
    });
  }, [enabled, boundTableKeys, rowsByTableKey, tablesSnap, tableTypes]);

  const rowCountByTargetId = useMemo(() => {
    if (!enabled || loading || unreadableTableLabels.length > 0) return {};

    return Object.fromEntries(boundTargetIds.map((targetId) => {
      const splitQuery = createSplitQueryFromTargetSource({
        targetSource: { tableId: splitTableId, columnId: splitColumnId },
        targetId,
      });
      const count = rowSources.reduce((total, source) => total + filterArRowIdsForRecipient({
        rowIds: source.rowIds,
        rowsDocs: source.rowsDocs,
        columns: source.columns,
        splitQuery,
        filterEntries: source.filterEntries,
        selectedRowIds: source.selectedRowIds,
      }).length, 0);
      return [targetId, count];
    }));
  }, [
    enabled,
    loading,
    unreadableTableLabels,
    boundTargetIds,
    splitTableId,
    splitColumnId,
    rowSources,
  ]);

  return { loading, unreadableTableLabels, rowCountByTargetId };
}
